/**
 * Feed Administration API Client
 *
 * Wraps tms-api-feedManagement endpoints.
 * Follows the same pattern as milk-providers.api.ts in the mothers module:
 * functions return Promise<AxiosResponse<T>> — the createApiClient interceptor
 * unwraps the { success, data, meta } envelope so callers access the payload
 * directly via response.data.
 */
import type { AxiosResponse } from 'axios';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES, config } from '@/core/config';
import type { FeedListResponse } from '@tms-phoenix/tms-types';
import type {
  ApiFeedDeliveryRecord,
  ApiFeedOrderDetail,
  ApiCreateFeedOrderResponse,
  ApiQuickFeedRecord,
  ApiQuickFeedCreateResponse,
  ApiReDosingRecord,
  ApiReDosingCreateResponse,
  ApiReDosingByContainerResponse,
  ApiReceivableLookupResponse,
  CreateFeedOrderRequest,
  RemoveFeedOrderRequest,
  CreateQuickFeedRequest,
  CreateReDosingRequest,
  AdministerFeedRequest,
  AdministerFeedResponse,
} from '../types';
import type { ApiContainerInfo } from '@/modules/feed-processing/types/feed-processing.types';

const PREFIX = API_ROUTES.FEED_MANAGEMENT;

/** Fallback hospital UUID used when VITE_HOSPITAL_ID is not set. */
const DEFAULT_HOSPITAL_ID = '00000000-0000-4000-a000-000000000007';

export function resolveHospitalId(override?: string): string {
  return override?.trim() || config.hospitalId?.trim() || DEFAULT_HOSPITAL_ID;
}

const api = getApiClient();

// ── Feed Orders (Feed Administration tab) ────────────────────────────────────

/**
 * GET /feedAdmin/deliveries
 *
 * List feed deliveries for the current hospital.
 * Returns flat rows (patient_name, mrn, feed_id, feed_time, order_date, volume_ml, status).
 * The interceptor unwraps the envelope so response.data is ApiFeedDeliveryRecord[].
 */
export function fetchFeedRecords(): Promise<AxiosResponse<ApiFeedDeliveryRecord[]>> {
  return api.get<ApiFeedDeliveryRecord[]>(`${PREFIX}/feedAdmin/deliveries`);
}

/**
 * GET /feedOrders/:order_id
 *
 * Return the full detail of a single feed order.
 * The detail shape includes a patient block with middle_name in addition to
 * the fields present on list items.
 */
export function fetchFeedOrderById(orderId: string): Promise<AxiosResponse<ApiFeedOrderDetail>> {
  return api.get<ApiFeedOrderDetail>(`${PREFIX}/feedOrders/${orderId}`);
}

/**
 * POST /feedOrders
 *
 * Create a new feed order. Allergen conflicts are checked server-side;
 * the response may include allergen warnings even on success.
 */
export function createFeedRecord(body: CreateFeedOrderRequest): Promise<AxiosResponse<ApiCreateFeedOrderResponse>> {
  return api.post<ApiCreateFeedOrderResponse>(`${PREFIX}/feedOrders`, body);
}

/**
 * PATCH /feedOrders/:order_id/remove
 *
 * Soft-delete a feed order (sets status to INACTIVE).
 * A required reason string is stored in special_instructions.
 */
export function removeFeedOrder(
  orderId: string,
  body: RemoveFeedOrderRequest,
): Promise<AxiosResponse<ApiFeedOrderDetail>> {
  return api.patch<ApiFeedOrderDetail>(`${PREFIX}/feedOrders/${orderId}/remove`, body);
}

// ── Feed Administration — administer ─────────────────────────────────────────

/**
 * POST /feedAdmin/
 *
 * Phase 1: Validates patient-container link + NPO check → creates PENDING delivery.
 * Phase 2: Runs allergen / expiry / recall safety checks → creates verification
 * (PASS), updates delivery to COMPLETED, updates feed_preparations to COMPLETED.
 *
 * Returns safety_checks so the UI can surface specific failures.
 */
export function administerFeed(
  body: AdministerFeedRequest,
): Promise<AxiosResponse<AdministerFeedResponse>> {
  return api.post<AdministerFeedResponse>(`${PREFIX}/feedAdmin`, body);
}

// ── Quick Feed ────────────────────────────────────────────────────────────────

/**
 * GET /quickFeeds
 *
 * List quick-feed preparations for a hospital.
 * Optional filters: patient_id, status, patient_name (partial match).
 */
export function fetchQuickFeedRecords(params?: {
  patient_id?: string;
  status?: string;
  patient_name?: string;
}): Promise<AxiosResponse<ApiQuickFeedRecord[]>> {
  return api.get<ApiQuickFeedRecord[]>(`${PREFIX}/quickFeeds`, {
    params: params ?? {},
  });
}

/**
 * POST /quickFeeds
 *
 * Creates a quick-feed preparation + delivery in one transaction.
 * Returns 409 if the patient has an active NPO feed order.
 */
export function createQuickFeed(
  body: CreateQuickFeedRequest,
): Promise<AxiosResponse<ApiQuickFeedCreateResponse>> {
  return api.post<ApiQuickFeedCreateResponse>(`${PREFIX}/quickFeeds`, body);
}

/** @deprecated Use createQuickFeed. */
export const createQuickFeedLink = createQuickFeed;

/**
 * GET /quickFeeds/receivable/:receipt_number
 *
 * Resolves a scanned receivable barcode (e.g. RCV-T1-DANIA-001) to its UUID
 * and metadata. Call this first, then pass receivable_id to POST /quickFeeds.
 */
export function resolveReceivable(
  receiptNumber: string,
): Promise<AxiosResponse<ApiReceivableLookupResponse>> {
  return api.get<ApiReceivableLookupResponse>(
    `${PREFIX}/quickFeeds/receivable/${encodeURIComponent(receiptNumber)}`,
  );
}

// ── Re-dosing ─────────────────────────────────────────────────────────────────

/**
 * GET /feedPrep/feeds
 *
 * List feed prep splits with patient, schedule, and status.
 * Pass patient_id to narrow to one patient; pass status='PREPARED' to get only
 * splits that are ready to be re-dosed (their split_id is the correct container_identifier
 * for POST /reDosing).
 */
export function fetchFeedPrepSplits(params?: {
  patient_id?: string;
  status?: string;
  limit?: number;
}): Promise<AxiosResponse<FeedListResponse>> {
  return api.get<FeedListResponse>(`${PREFIX}/feedPrep/feeds`, {
    params: params ?? {},
  });
}

/**
 * GET /reDosing
 *
 * List re-dose entries grouped by patient + preparation for a hospital.
 */
export function fetchReDosingRecords(): Promise<AxiosResponse<ApiReDosingRecord[]>> {
  return api.get<ApiReDosingRecord[]>(`${PREFIX}/reDosing`);
}

/**
 * POST /reDosing
 *
 * Re-splits a container into new bottles. Sum of bottle volumes +
 * remaining_volume must equal original_volume.
 */
export function createReDosing(
  body: CreateReDosingRequest,
): Promise<AxiosResponse<ApiReDosingCreateResponse>> {
  return api.post<ApiReDosingCreateResponse>(`${PREFIX}/reDosing`, body);
}

/**
 * GET /reDosing/splits
 *
 * Returns all bottle splits that were created when re-dosing the given source container.
 * Pass the barcode of the original bottle (e.g. BTL-S202600001-02).
 */
export function fetchReDosingByContainer(params: {
  container_identifier: string;
}): Promise<AxiosResponse<ApiReDosingByContainerResponse>> {
  const { container_identifier } = params;
  return api.get<ApiReDosingByContainerResponse>(`${PREFIX}/reDosing/splits`, {
    params: {
      container_identifier,
    },
  });
}

/**
 * GET /feedPrep/container/:containerIdentifier
 *
 * Returns full preparation details for a container barcode (e.g. BTL-S202600001-01).
 * hospital_id is required.
 */
export function fetchContainerById(
  containerIdentifier: string,
): Promise<AxiosResponse<ApiContainerInfo>> {
  return api.get(
    `${PREFIX}/feedPrep/container/${encodeURIComponent(containerIdentifier)}`,
  );
}
